<template>
  <teleport to="body">
    <transition name="ultra-fade">
      <div
        v-if="visible"
        class="ultra-overlay"
        tabindex="0"
        ref="overlayRef"
        @keydown.esc="close"
        @click.self="close"
      >
        <!-- Content area -->
        <div class="ultra-content" @click.self="close">
          <!-- Image -->
          <img
            v-if="mediaType === 'image'"
            :src="url"
            alt=""
            class="ultra-media"
            draggable="false"
          />
          <!-- Video -->
          <video
            v-else-if="mediaType === 'video'"
            :src="url"
            :poster="poster"
            class="ultra-media ultra-video"
            controls
            playsinline
            autoplay
          ></video>
          <!-- PDF -->
          <iframe
            v-else-if="mediaType === 'pdf'"
            :src="url"
            class="ultra-pdf"
          ></iframe>
        </div>

        <!-- Close button (after content so it paints on top) -->
        <button
          type="button"
          class="ultra-close cleanIconButton"
          @click.stop="close"
          aria-label="Close full-screen viewer"
        >
          <svg class="close verySmall" aria-hidden="true">
            <use href="@/assets/icons/iconset.svg#cross_smaller"></use>
          </svg>
        </button>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref, watch, nextTick, onUnmounted } from "vue";

export default {
  name: "UltraOverlay",
  props: {
    visible: { type: Boolean, default: false },
    url: { type: String, default: "" },
    poster: { type: String, default: "" },
    mediaType: {
      type: String,
      default: "image",
      validator: (v) => ["image", "video", "pdf"].includes(v),
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const overlayRef = ref(null);

    const close = () => emit("close");

    // Auto-focus overlay for keyboard events
    watch(
      () => props.visible,
      (val) => {
        if (val) {
          nextTick(() => overlayRef.value?.focus());
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      },
    );

    onUnmounted(() => {
      document.body.style.overflow = "";
    });

    return { overlayRef, close };
  },
};
</script>

<style scoped>
.ultra-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.ultra-close {
  position: fixed;
  top: var(--space-5, 16px);
  right: var(--space-5, 16px);
  z-index: 100001;
  width: var(--control-height-compact, 36px);
  height: var(--control-height-compact, 36px);
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.ultra-close svg {
  fill: #fff;
  stroke: #fff;
  color: #fff;
}

.ultra-close:hover {
  opacity: 0.7;
}

.ultra-close:focus-visible {
  outline: 2px solid var(--color-accent-primary, #8b5cf6);
  outline-offset: 3px;
}

.ultra-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ultra-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.ultra-video {
  background: #000;
}

.ultra-pdf {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}

/* Fade transition */
.ultra-fade-enter-active,
.ultra-fade-leave-active {
  transition: opacity 0.2s ease;
}

.ultra-fade-enter-from,
.ultra-fade-leave-to {
  opacity: 0;
}
</style>
